/**
 * What the offering listing does when a request does not produce an answer (task-djasurv8m7).
 *
 * ⛔⛔ ONE FAILURE PATH, NOT ONE PER FAILURE MODE. The first version of this fix handled the
 * HTTP-error branch and left the CATCH branch (network failure, timeout, a `response.json()` that
 * cannot decode) still showing the previous page under the operator's new search term — the SAME
 * silent widening, reached through a different door. My own note on the first half said "THE
 * WIDENING WAS IN THE SILENCE, NOT THE QUERY", and the catch is another silence.
 * 🔑 WHEN A FIX KEEPS SPROUTING THE SAME DEFECT ONE BRANCH OVER, THE FRAME IS THE BUG. So the
 *   behaviour lives here once and both branches call it; a third failure mode cannot diverge
 *   because there is nowhere for it to diverge to.
 *
 * ⛔ EXTRACTED SO IT CAN BE EXECUTED BY A TEST rather than pattern-matched. Inline in a
 * ~4,900-line component the only available assertion was "the source mentions setProducts([])",
 * which cannot tell a wired branch from an unwired one.
 */
export interface ListingFailureHandlers {
  /** Clears the rows so a stale page cannot pose as the result of the current filters. */
  setProducts: (rows: never[]) => void;
  /** Clears the count, so "Showing 1-50 of 8201" cannot outlive the request that produced it. */
  setPagination: (p: null) => void;
  /** Non-null makes the empty state say THE QUERY FAILED rather than "nothing matched". */
  setListingError: (detail: string) => void;
  toast: (t: { title: string; description: string; variant: 'destructive' }) => void;
}

/**
 * @param detail  operator-facing cause, e.g. "HTTP 500 — Failed to fetch offering information"
 *                or "Network error — Failed to fetch".
 */
export function applyListingFailure(detail: string, h: ListingFailureHandlers): void {
  h.setListingError(detail);
  h.setProducts([]);
  h.setPagination(null);
  h.toast({
    title: 'Search failed',
    // ⇒ Names the consequence, not just the error: the operator's next action is Select All, and
    //   they need to know the rows on screen are not an answer to their filters.
    description: `The product list could not be loaded (${detail}). Nothing below is filtered by your search — retry before acting on it.`,
    variant: 'destructive',
  });
}

/** A human-readable cause for a thrown error, without leaking an object into the UI. */
export function describeThrownFailure(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error ?? 'unknown error');
  return `Network error — ${msg}`;
}
